import moment from 'moment';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { SERVICE_TYPE_CHOICES } from '@rover/constants/src/translations/service.constants';
import Boarding from '@rover/icons/svg/boarding.svg';
import Daycare from '@rover/icons/svg/daycare.svg';
import DropIn from '@rover/icons/svg/drop-in.svg';
import Sitting from '@rover/icons/svg/sitting.svg';
import Walking from '@rover/icons/svg/walking.svg';
import { DateTimeFormatType } from '@rover/react-lib/src/context/I18nContext';
import dayFactory from '@rover/react-lib/src/factories/dayFactory';
import { DayEnum } from '@rover/rsdk/src/apiClient/latest/model/dayEnum';
import type { DateRangeField } from '@rover/types/src/datetime/DateRange';
import type { Day } from '@rover/types/src/datetime/Day';
import getDateTimeFormatMapForLocale from '@rover/utilities/datetime';

export const getServiceTypeIcon = (
  service: string | undefined,
  color: string
): JSX.Element | null => {
  const iconProps = { color, width: '24px', height: '24px' };

  const iconMap: Record<string, JSX.Element> = {
    [SERVICE_TYPE_CHOICES.boarding]: <Boarding {...iconProps} />,
    [SERVICE_TYPE_CHOICES.traveling]: <Sitting {...iconProps} />,
    [SERVICE_TYPE_CHOICES.dropIn]: <DropIn {...iconProps} />,
    [SERVICE_TYPE_CHOICES.dayCare]: <Daycare {...iconProps} />,
    [SERVICE_TYPE_CHOICES.walking]: <Walking {...iconProps} />,
  };

  return service ? iconMap[service] || null : null;
};

export const makeDayFromDate = (date: Date, DF: DateTimeFormatType): Day => {
  return dayFactory({
    name: moment(date).format(DF.MONTH_DAY_MED),
    date,
  });
};

// Format date range for display
export const formatDateRange = (
  startDate: DateRangeField,
  endDate: DateRangeField,
  locale: Locale
): string => {
  if (!startDate) return '';

  const DF = getDateTimeFormatMapForLocale(locale);
  const asMoment = (date: Date): moment.Moment => moment(date).locale(locale);
  const formatDate = (date: moment.Moment, includeYear: boolean = false): string =>
    date.format(includeYear ? DF.DATE_MED : DF.MONTH_DAY_MED);

  const start = asMoment(startDate);
  const end = endDate ? asMoment(endDate) : undefined;

  const showYear = (end && start.year() !== end.year()) || start.year() !== moment().year();

  // If no end date or same day, show single date
  if (!end || start.valueOf() === end.valueOf()) {
    return formatDate(start, showYear);
  }

  // Different months, or a year to spell out, so each end carries its own month
  if (showYear || start.month() !== end.month() || start.year() !== end.year()) {
    return `${formatDate(start, showYear)} - ${formatDate(end, showYear)}`;
  }

  // Same month, so the month is written once — on whichever side of the range the
  // locale's own format puts it, since `Jun 10 - 20` reads `10 - 20 Jun` where the
  // day comes first.
  const startFormatted = formatDate(start);
  const isMonthFirst = /^\D/.test(startFormatted);

  return isMonthFirst
    ? `${startFormatted} - ${end.date()}`
    : `${start.date()} - ${formatDate(end)}`;
};

export const formatSelectedDays = (selectedDays: Day[]): Record<DayEnum, boolean> => {
  const days = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  };
  selectedDays.forEach(({ name }) => {
    days[name.toLowerCase()] = true;
  });
  return days;
};
